import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, Heart, Users, Sparkles, ShieldCheck, BookOpen, Stethoscope, Leaf, Baby } from "lucide-react";
import { api } from "../lib/api";

const HERO_IMG = "https://images.pexels.com/photos/1427430/pexels-photo-1427430.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const ABOUT_IMG = "https://images.pexels.com/photos/30124240/pexels-photo-30124240.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const FOCUS = [
  { icon: BookOpen, title: "Child Education", img: "https://images.pexels.com/photos/18012459/pexels-photo-18012459.jpeg?auto=compress&cs=tinysrgb&h=500&w=700" },
  { icon: Stethoscope, title: "Healthcare", img: "https://images.pexels.com/photos/19438562/pexels-photo-19438562.jpeg?auto=compress&cs=tinysrgb&h=500&w=700" },
  { icon: Leaf, title: "Environment", img: "https://images.pexels.com/photos/20356926/pexels-photo-20356926.jpeg?auto=compress&cs=tinysrgb&h=500&w=700" },
  { icon: Baby, title: "Nutrition", img: "https://images.pexels.com/photos/9345661/pexels-photo-9345661.jpeg?auto=compress&cs=tinysrgb&h=500&w=700" },
];

export default function Home() {
  const [stats, setStats] = useState({ total_raised: 0, total_donors: 0, lives_impacted: 0 });

  useEffect(() => {
    api.get("/donations/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <div className="fade-in">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20 grid md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-5 order-2 md:order-1">
          <div className="inline-flex items-center gap-2 bg-[#F3EFE7] px-3 py-1 rounded-full text-xs font-semibold text-[#000080] mb-5" data-testid="hero-badge">
            <Sparkles className="w-3.5 h-3.5" /> 80G & 12A Certified NGO
          </div>
          <h1 className="serif text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] text-[#000080]">
            Every child deserves a <em className="text-[#FF9933] not-italic">tomorrow</em> worth dreaming of.
          </h1>
          <p className="mt-5 text-base md:text-lg text-[#4A4A4A] leading-relaxed">
            Adopt Helpless Children Charitable Trust works across India to give underprivileged children access to education, healthcare, nutrition, and dignity — one life at a time.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/donate" data-testid="hero-donate-cta">
              <Button className="rounded-full bg-[#FF9933] hover:bg-[#E68A2E] text-white px-7 h-12 btn-glow">
                <Heart className="w-4 h-4 mr-2" /> Donate Now
              </Button>
            </Link>
            <Link to="/what-we-do" data-testid="hero-learn-cta">
              <Button variant="outline" className="rounded-full border-[#000080] text-[#000080] px-7 h-12 hover:bg-[#000080] hover:text-white">
                See Our Work <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-4">
            <Stat label="Raised" value={`₹${stats.total_raised.toLocaleString("en-IN")}`} />
            <Stat label="Donors" value={stats.total_donors} />
            <Stat label="Lives Touched" value={stats.lives_impacted} />
          </div>
        </div>

        <div className="md:col-span-7 order-1 md:order-2 relative">
          <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
            <img src={HERO_IMG} alt="Children smiling" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#000080]/30 via-transparent to-transparent" />
          </div>
          <div className="hidden md:block absolute -bottom-8 -left-8 bg-white rounded-2xl shadow-xl p-5 w-64 border border-[#eae5d9]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF9933]/15 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#FF9933]" />
              </div>
              <div>
                <div className="serif text-2xl font-semibold text-[#000080]">5,000+</div>
                <div className="text-xs text-[#737373]">Children supported</div>
              </div>
            </div>
          </div>
          <div className="hidden md:block absolute -top-6 -right-6 bg-[#138808] text-white rounded-2xl shadow-xl p-4 w-48">
            <ShieldCheck className="w-5 h-5" />
            <div className="text-sm font-bold mt-1">100% Transparent</div>
            <div className="text-xs text-white/80">Every rupee tracked</div>
          </div>
        </div>
      </section>

      {/* FOCUS AREAS */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-16">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#FF9933] font-bold mb-2">Our Focus</div>
            <h2 className="serif text-3xl md:text-4xl font-semibold text-[#000080] ribbon">Programs that change lives</h2>
          </div>
          <Link to="/what-we-do" className="text-sm font-semibold text-[#000080] hover:text-[#FF9933]" data-testid="home-viewall">
            View all programs →
          </Link>
        </div>
        <div className="grid md:grid-cols-4 gap-6">
          {FOCUS.map((f, i) => (
            <Link to="/what-we-do" key={i} className="group card-hover bg-white rounded-2xl overflow-hidden border border-[#eae5d9]" data-testid={`focus-${f.title.toLowerCase()}`}>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={f.img} alt={f.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-5">
                <f.icon className="w-5 h-5 text-[#FF9933] mb-2" strokeWidth={1.6} />
                <div className="serif text-xl font-semibold text-[#000080]">{f.title}</div>
                <div className="text-sm text-[#737373] mt-1">Learn how you can contribute</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ABOUT STRIP */}
      <section className="bg-[#F3EFE7] border-y border-[#eae5d9]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 grid md:grid-cols-2 gap-10 items-center">
          <div className="aspect-[5/4] rounded-3xl overflow-hidden shadow-xl">
            <img src={ABOUT_IMG} alt="Community" className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#138808] font-bold mb-2">Who We Are</div>
            <h2 className="serif text-3xl md:text-4xl font-semibold text-[#000080] mb-4">Small acts. Lifelong ripples.</h2>
            <p className="text-[#4A4A4A] leading-relaxed mb-6">
              Founded on the belief that every child is a whole universe, AHCCT walks alongside families and communities across India — building classrooms, stocking medicine shelves, training mothers, and planting forests. We don't hand out charity; we build futures.
            </p>
            <Link to="/about" data-testid="home-about-cta">
              <Button variant="outline" className="rounded-full border-[#000080] text-[#000080] hover:bg-[#000080] hover:text-white">
                Our Story <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-16">
        <div className="rounded-3xl p-10 md:p-16 text-center text-white overflow-hidden relative" style={{ background: "linear-gradient(135deg,#000080 0%,#1a1a6e 50%,#000080 100%)" }}>
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#FF9933]/20 blur-3xl" />
          <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-[#138808]/20 blur-3xl" />
          <div className="relative">
            <h2 className="serif text-3xl md:text-5xl font-semibold mb-4 text-white">₹51 can feed a child for a day.</h2>
            <p className="text-white/80 max-w-2xl mx-auto">Your donation is 100% tax-deductible under Section 80G. Transparent, trackable, impactful.</p>
            <Link to="/donate" className="inline-block mt-8" data-testid="home-cta-donate">
              <Button className="rounded-full bg-[#FF9933] hover:bg-[#E68A2E] text-white px-10 h-14 text-base btn-glow">
                <Heart className="w-5 h-5 mr-2" /> Donate Now
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border-l-2 border-[#FF9933] pl-3">
      <div className="serif text-2xl md:text-3xl font-semibold text-[#000080]">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-[#737373] mt-0.5">{label}</div>
    </div>
  );
}
